import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UserService } from './user.service';
import { Observable, shareReplay, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { ToastService } from '../shared/toast/toast.service';

@Injectable({
  providedIn: 'root',
})
export abstract class BaseAPIService {
  private readonly serverURL = environment.serverURL;
  private readonly version = environment.version;
  private httpClient = inject(HttpClient);
  public toastService = inject(ToastService);

  public static cache: Record<string, Observable<any>> = {};

  protected get baseURL(): string {
    return this.serverURL + this.version;
  }

  protected getBlob(endpoint: string): Observable<Blob> {
    return this.httpClient.get<Blob>(
      `${this.baseURL}${endpoint}`, { withCredentials: true, responseType: 'blob' as 'json' });
  }

  protected get<T>(endpoint: string, cache: boolean = false, asString?: boolean): Observable<T> {
    if (cache) {
      if (!BaseAPIService.cache[endpoint]) {
        BaseAPIService.cache[endpoint] = this.httpClient
          .get<T>(`${this.baseURL}${endpoint}`, { withCredentials: true })
          .pipe(shareReplay(1));
      }
      return BaseAPIService.cache[endpoint];
    }
    let extraOptions = {};
    if (!!asString) {
      extraOptions = { responseType: 'text' as 'json' };
    }
    return this.httpClient
      .get<T>(`${this.baseURL}${endpoint}`, { withCredentials: true, ...extraOptions })
      .pipe(
        tap({
          next: (response: T) => {
            if (typeof response === 'string' && response.trim()) {
              this.toastService.success(response.trim());
            }
          },
          error: (error: any) => {
            if (error?.message) {
              //this.toastService.error(error.message);
            } else {
              //this.toastService.error('Something went wrong');
            }
          },
        }),
      );
  }

  protected post<T>(
    endpoint: string,
    body: any,
    cache: boolean = false,
    asString?: boolean,
  ): Observable<T> {
    if (cache) {
      if (!BaseAPIService.cache[endpoint]) {
        BaseAPIService.cache[endpoint] = this.httpClient
          .post<T>(`${this.baseURL}${endpoint}`, body, { withCredentials: true })
          .pipe(shareReplay(1));
      }
      return BaseAPIService.cache[endpoint];
    }
    let extraOptions = {};
    if (!!asString) {
      extraOptions = { responseType: 'text' as 'json' };
    }
    return this.httpClient.post<T>(`${this.baseURL}${endpoint}`, body, {
      withCredentials: true,
      ...extraOptions,
    }).pipe(
        tap({
          next: (response: T) => {
            if (typeof response === 'string' && response.trim()) {
              this.toastService.success(response.trim());
            }
          },
          error: (error: any) => {
            if (error?.message) {
              //this.toastService.error(error.message);
            } else {
              //this.toastService.error('Something went wrong');
            }
          },
        }),
      );
  }

  protected put<T>(
    endpoint: string,
    body: any,
    cache: boolean = false,
    asString?: boolean,
  ): Observable<T> {
    if (cache) {
      if (!BaseAPIService.cache[endpoint]) {
        BaseAPIService.cache[endpoint] = this.httpClient
          .put<T>(`${this.baseURL}${endpoint}`, body, { withCredentials: true })
          .pipe(shareReplay(1));
      }
      return BaseAPIService.cache[endpoint];
    }
    let extraOptions = {};
    if (!!asString) {
      extraOptions = { responseType: 'text' as 'json' };
    }
    return this.httpClient.put<T>(`${this.baseURL}${endpoint}`, body, {
      withCredentials: true,
      ...extraOptions,
    }).pipe(
        tap({
          next: (response: T) => {
            if (typeof response === 'string' && response.trim()) {
              this.toastService.success(response.trim());
            }
          },
          error: (error: any) => {
            if (error?.message) {
              //this.toastService.error(error.message);
            } else {
              //this.toastService.error('Something went wrong');
            }
          },
        }),
      );
  }

  protected patch<T>(
    endpoint: string,
    body: any,
    cache: boolean = false,
    asString?: boolean,
  ): Observable<T> {
    if (cache) {
      if (!BaseAPIService.cache[endpoint]) {
        BaseAPIService.cache[endpoint] = this.httpClient
          .patch<T>(`${this.baseURL}${endpoint}`, body, { withCredentials: true })
          .pipe(shareReplay(1));
      }
      return BaseAPIService.cache[endpoint];
    }
    let extraOptions = {};
    if (!!asString) {
      extraOptions = { responseType: 'text' as 'json' };
    }
    return this.httpClient.patch<T>(`${this.baseURL}${endpoint}`, body, {
      withCredentials: true,
      ...extraOptions,
    }).pipe(
        tap({
          next: (response: T) => {
            if (typeof response === 'string' && response.trim()) {
              this.toastService.success(response.trim());
            }
          },
          error: (error: any) => {
            if (error?.message) {
              //this.toastService.error(error.message);
            } else {
              //this.toastService.error('Something went wrong');
            }
          },
        }),
      );
  }

  protected delete<T>(endpoint: string, cache: boolean = false, asString?: boolean): Observable<T> {
    if (cache) {
      if (!BaseAPIService.cache[endpoint]) {
        BaseAPIService.cache[endpoint] = this.httpClient
          .delete<T>(`${this.baseURL}${endpoint}`, { withCredentials: true })
          .pipe(shareReplay(1));
      }
      return BaseAPIService.cache[endpoint];
    }
    let extraOptions = {};
    if (!!asString) {
      extraOptions = { responseType: 'text' as 'json' };
    }
    return this.httpClient.delete<T>(`${this.baseURL}${endpoint}`, {
      withCredentials: true,
      ...extraOptions,
    }).pipe(
        tap({
          next: (response: T) => {
            if (typeof response === 'string' && response.trim()) {
              this.toastService.success(response.trim());
            }
          },
          error: (error: any) => {
            if (error?.message) {
              //this.toastService.error(error.message);
            } else {
              //this.toastService.error('Something went wrong');
            }
          },
        }),
      );
  }

  public static clearCache(): void {
    BaseAPIService.cache = {};
  }
}
